import { createSlice, createAction, nanoid } from '@reduxjs/toolkit';
import { RootState } from '../index';

interface NotificationsState {
  [key: string]: ILongPullUIItem[];
}

const EMPTY_ARRAY: ILongPullUIItem[] = [];
export const NOTIFICATIONS_FEATURE_KEY = 'notifications';

// ACTIONS
export const addNotification =
  createAction<{ type: ILongPullUIItem['type']; message: string; key?: string }>('ADD_NOTIFICATION');
export const removeNotification = createAction<{ key?: string; notificationId: string }>('REMOVE_NOTIFICATION');
export const clearNotifications = createAction<{ key?: string }>('CLEAR_NOTIFICATIONS');
export const addLongPullUIItem = createAction<{ key?: string; item: ILongPullUIItem }>('ADD_ITEM_TO_QUEUE');
export const updateLongPullUIItem = createAction<{
  key?: string;
  itemId: number | string;
  updates: Partial<ILongPullUIItem>;
}>('UPDATE_ITEM_IN_QUEUE');
export const removeLongPullUIItem = createAction<{ key?: string; itemId: string | number }>('REMOVE_ITEM_FROM_QUEUE');
export const clearLongPullUIQueue = createAction<{ key?: string }>('CLEAR_LONG_PULL_UI_QUEUE');

const initialState: NotificationsState = {};

// Helper to standardize keys
const normalizeKey = (key?: string) => (key ?? 'global').trim();

const notificationsSlice = createSlice({
  name: NOTIFICATIONS_FEATURE_KEY,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(addNotification, (state, { payload }) => {
      const { type, message } = payload;
      const key = normalizeKey(payload.key);

      if (!message || !type) {
        console.error("Can't add notification: 'type' and 'message' are required");
        return;
      }

      if (!state[key]) {
        state[key] = [];
      }

      state[key].push({ id: nanoid(), type, message } as ILongPullUIItem);
    });

    builder.addCase(removeNotification, (state, { payload }) => {
      const { notificationId } = payload;
      const key = normalizeKey(payload.key);

      if (!notificationId) {
        console.error("Can't remove notification: no notificationId provided");
        return;
      }

      if (!state[key]) {
        console.error(`Can't remove notification: no notifications found for key '${key}'`);
        return;
      }

      state[key] = state[key].filter((notification) => notification.id !== notificationId);

      if (state[key].length === 0) {
        delete state[key];
      }
    });

    builder.addCase(clearNotifications, (state, { payload }) => {
      const key = normalizeKey(payload.key);

      if (state[key]) {
        delete state[key];
      }
    });

    builder.addCase(addLongPullUIItem, (state, { payload }) => {
      const key = normalizeKey(payload.key);
      const { item } = payload;

      if (!item || !item.id) {
        console.error("Can't add to the queue: item with valid 'id' is required");
        return;
      }

      if (!state[key]) {
        state[key] = [];
      }

      const itemExists = state[key].some((existingItem) => existingItem.id === item.id);
      if (itemExists) {
        return;
      }

      state[key].push({ ...item, isLongpull: true });
    });

    builder.addCase(updateLongPullUIItem, (state, { payload }) => {
      if (!payload) {
        console.error("Can't update the queue: no payload provided");
        return;
      }

      const { itemId, updates } = payload;
      const key = normalizeKey(payload.key);

      if (!updates || Object.keys(updates).length === 0) {
        console.error("Can't update the queue: no updates provided");
        return;
      }

      if (!state[key]) {
        console.error(`Queue for key '${key}' does not exist`);
        return;
      }

      const index = state[key].findIndex((queueItem) => queueItem.id === itemId);
      if (index === -1) {
        console.error(`Item with id '${itemId}' not found in the queue for key '${key}'`);
        return;
      }

      const existingItem = state[key][index];

      // Apply only valid updates
      for (const prop in updates) {
        if (prop !== 'id' && prop in existingItem) {
          // @ts-expect-error
          existingItem[prop] = updates[prop];
        }
      }
    });

    builder.addCase(removeLongPullUIItem, (state, { payload }) => {
      if (!payload) {
        console.error("Can't remove from the queue: no payload provided");
        return;
      }

      const { itemId } = payload;
      const key = normalizeKey(payload.key);

      if (!itemId) {
        console.error("Can't remove from the queue: no itemId provided");
        return;
      }

      if (!state[key]) {
        console.error(`Can't remove from the queue: no queue exists for key '${key}'`);
        return;
      }

      state[key] = state[key].filter((queueItem) => queueItem.id !== itemId);

      if (state[key].length === 0) {
        delete state[key];
      }
    });

    builder.addCase(clearLongPullUIQueue, (state, { payload }) => {
      const key = payload?.key ? normalizeKey(payload.key) : null;

      if (key) {
        if (!state[key]) {
          console.error(`Can't clear queue: no queue exists for key '${key}'`);
          return;
        }
        delete state[key];
      } else {
        for (const k in state) {
          delete state[k];
        }
      }
    });
  },
});

export const notificationsReducer = notificationsSlice.reducer;

// SELECTORS
export const getNotificationsState = (rootState: RootState) => rootState[NOTIFICATIONS_FEATURE_KEY];

export const notificationsSelectors = {
  // getNotificationsByKey: (state: RootState, key: string) => getNotificationsState(state)[normalizeKey(key)] || [],
  getNotificationsByKey: (state: RootState, key: string) =>
    getNotificationsState(state)[normalizeKey(key)] ?? EMPTY_ARRAY,
};
